/**
 * Transport HTTP brut — la couche que la file de synchronisation et `apiFetch`
 * se partagent (`ARCHITECTURE_V2.md` §3.3, étape 3).
 *
 * 🔴 **Ce fichier existe pour supprimer un cycle d'imports, et le sujet mérite
 * une ligne.** Le §3.5 désigne `api/client.ts` comme « le point de passage
 * unique de toutes les requêtes » et donc l'endroit où la file se branche. Mais
 * la file doit elle-même **émettre** des requêtes au moment du drainage : si
 * elle appelait `api/client.ts`, les deux modules s'importeraient l'un l'autre.
 * Le cycle serait techniquement inoffensif — aucun des deux ne lit l'autre à
 * l'évaluation — mais il est facile à casser sans s'en rendre compte, et il
 * n'apporte rien.
 *
 * La couche est donc à part, et la dépendance est **à sens unique** :
 *
 * ```
 *   api/transport.ts   ← lib/syncQueue.ts   ← api/client.ts
 *   (émettre)             (mettre en file)     (aiguiller)
 * ```
 *
 * ⚠️ **Rien n'importe ce fichier directement, hors ces deux-là.** Les neuf
 * modules d'API et tous les écrans continuent d'importer `api/client`, qui
 * réexporte ce qu'il faut : le déplacement est invisible pour eux, et c'était la
 * condition pour ne pas toucher aux neuf modules (§3.5).
 *
 * 🔴 **Deux natures d'échec, posées à l'étape 1 et inchangées ici.** Le critère
 * est « **le serveur a-t-il répondu ?** », jamais la classe de statut : un 500
 * est une réponse. Ne pas fusionner ce seuil avec celui du §3.3, qui décide de
 * ce qui est **rejouable** (408, 429, 5xx) — « faut-il réessayer plus tard » et
 * « faut-il défaire tout de suite ce que l'utilisateur voit » ne se répondent
 * pas pareil.
 */

import type { ApiErrorResponse } from '../../../shared/types';

/**
 * Le serveur a répondu, et il refuse.
 *
 * Porte le statut HTTP et le `code` applicatif : c'est ce qui permet à un écran
 * de reconnaître un cas précis (`food_in_use`, `same_meal`…) plutôt que de
 * n'afficher qu'un message.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Le serveur n'a pas répondu — ou sa réponse est inexploitable.
 *
 * Couvre l'appareil hors ligne, le VPS injoignable, le TLS qui échoue, et le
 * cas où un intermédiaire (Caddy) renvoie une page HTML là où on attend du
 * JSON : dans tous ces cas, **rien ne dit que la requête était mauvaise**, donc
 * rien ne justifie de défaire ce que l'utilisateur vient de saisir.
 *
 * ⚠️ `cause` conserve l'erreur d'origine — un `TypeError: Failed to fetch` ne
 * dit rien à un utilisateur, mais il dit beaucoup dans une console.
 */
export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

/**
 * La mutation a été **mise en file** : elle partira au retour du réseau (§3.3).
 *
 * 🔴 **Dérive de `NetworkError`, et c'est délibéré.** Tout le front sait déjà
 * quoi faire d'un échec de transport depuis l'étape 1 : conserver l'aperçu
 * optimiste (`hooks/useDay.ts`), préférer les données au message
 * (`readError`), afficher une note **neutre** et non un bandeau rouge
 * (`ReadNote`). Une classe indépendante aurait obligé à retoucher chacun de ces
 * points ; en héritant, la mise en file bénéficie du bon comportement partout,
 * et là où le message compte on la distingue par `instanceof`.
 *
 * ⚠️ Ce n'est pas un échec. C'est un **accusé de prise en charge** — et c'est
 * pourquoi le message ne parle ni de panne ni de ressaisie.
 */
export class QueuedError extends NetworkError {
  constructor() {
    super('Saisie mise en file : elle partira au retour du réseau.');
    this.name = 'QueuedError';
  }
}

/** Vrai si l'erreur est un refus d'authentification. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * Le transport a échoué : la requête n'a pas atteint le serveur, sa réponse
 * n'était pas exploitable, **ou** elle a été mise en file.
 *
 * 🔴 **C'est le seul prédicat qui décide de conserver un aperçu optimiste.**
 */
export function isTransportFailure(error: unknown): boolean {
  return error instanceof NetworkError;
}

/** La mutation est en file d'attente — distinct d'une panne de réseau nue. */
export function isQueued(error: unknown): boolean {
  return error instanceof QueuedError;
}

/**
 * Le serveur a répondu et refuse la requête pour ce qu'elle est.
 *
 * Un rejeu à l'identique produirait le même refus : c'est ce qui distingue ce
 * cas d'une panne passagère, et c'est pourquoi l'aperçu correspondant doit être
 * annulé et le message présenté.
 */
export function isApplicationError(error: unknown): boolean {
  return error instanceof ApiError;
}

/* ==========================================================================
 * Émission
 * ======================================================================= */

/** Ce qu'une requête peut produire, sans lever. */
export type RawOutcome =
  | { kind: 'ok'; payload: unknown }
  | { kind: 'api'; status: number; code: string; message: string }
  | { kind: 'network'; message: string; cause: unknown }
  | { kind: 'abort' };

function isErrorPayload(value: unknown): value is ApiErrorResponse {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const inner: unknown = (value as { error: unknown }).error;
  return typeof inner === 'object' && inner !== null && 'message' in inner && 'code' in inner;
}

/** Une annulation volontaire n'est ni une panne de réseau ni un refus du serveur. */
function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Émet une requête et **décrit** ce qui s'est passé, sans jamais lever.
 *
 * ⚠️ C'est la forme dont la file a besoin : elle doit **décider** en fonction du
 * statut (rejouable, définitif, 401 qui suspend), pas attraper des exceptions
 * et les re-classer. `apiFetch` retraduit ce résultat en `throw` pour les
 * appelants, qui eux préfèrent des exceptions.
 */
export async function performFetch(path: string, init?: RequestInit): Promise<RawOutcome> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        // Le `content-type` n'est posé que s'il y a un corps : Fastify rejette un
        // POST annoncé en JSON dont le corps est vide.
        ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...init?.headers,
      },
    });
  } catch (cause) {
    // ⚠️ `fetch` ne lève que si **rien** n'est revenu : hors ligne, DNS, TLS,
    // connexion coupée. Une réponse d'erreur, elle, résout normalement.
    if (isAbort(cause)) return { kind: 'abort' };
    return { kind: 'network', message: 'Réseau indisponible.', cause };
  }

  const text = await response.text();

  /*
   * ⚠️ Le corps est analysé **sous garde**. Sans elle, une page d'erreur HTML
   * d'un intermédiaire — un 502 de Caddy pendant un redémarrage de l'API —
   * faisait lever un `SyntaxError` brut *avant* même que `response.ok` ne soit
   * consulté : l'appelant recevait une erreur d'analyse là où il y avait un
   * statut parfaitement lisible à lui donner.
   */
  let payload: unknown = null;
  if (text !== '') {
    try {
      payload = JSON.parse(text);
    } catch (cause) {
      if (!response.ok) {
        return {
          kind: 'api',
          status: response.status,
          code: 'http_error',
          message: `HTTP ${String(response.status)}`,
        };
      }
      // Succès annoncé, corps inutilisable : l'écriture a probablement eu lieu,
      // mais nous ne savons pas la lire. C'est une panne de transport, pas un
      // refus — donc rien à défaire à l'écran.
      return { kind: 'network', message: 'Réponse illisible du serveur.', cause };
    }
  }

  if (!response.ok) {
    if (isErrorPayload(payload)) {
      return {
        kind: 'api',
        status: response.status,
        code: payload.error.code,
        message: payload.error.message,
      };
    }
    return {
      kind: 'api',
      status: response.status,
      code: 'http_error',
      message: `HTTP ${String(response.status)}`,
    };
  }

  return { kind: 'ok', payload };
}

/**
 * Émission d'une mutation par la file, **corps déjà résolu**.
 *
 * ⚠️ N'ajoute **aucun** horodatage d'émission : celui de l'opération a été posé
 * à la mise en file, et c'est bien celui-là qu'il faut rejouer. Le reposer ici
 * daterait la décision au moment du drainage — soit exactement l'inversion que
 * le §3.4 interdit.
 */
export function sendRaw(method: string, path: string, body: unknown): Promise<RawOutcome> {
  return performFetch(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}
